import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n";
import usePageMeta from "../hooks/usePageMeta";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  ArrowRight,
  Compass,
  Layers,
  Workflow,
  Database,
  ListChecks,
  CheckCircle2,
  GitBranch,
  Microscope,
  Target,
  BookOpen,
  ShieldCheck,
} from "lucide-react";

const SITE = "https://strategy-landing-hub.emergent.host";

// ==================== PT/EN content ====================
const C = {
  pt: {
    seo: {
      title: "STRATFLOW® - Metodologia de Transformação de Negócios | GT Strategy",
      description:
        "STRATFLOW® é a metodologia proprietária da GT Strategy para transformação de negócios. Combina Lean, Six Sigma, TOC, Design Thinking e PMI em três movimentos: clarificar o valor, organizar o fluxo e ancorar na cultura. Inclui HandOff Mapping para integração de processos, dados e plataformas.",
    },
    nav_back: "Voltar para a página inicial",
    hero: {
      eyebrow: "Metodologia proprietária",
      title_a: "STRATFLOW",
      title_em: "®",
      title_b: "A engenharia de valor",
      title_c: "por trás da transformação.",
      lede:
        "STRATFLOW® é a metodologia que a GT Strategy aplica para conectar estratégia, operações, tecnologia e pessoas em um único fluxo de valor. Não é um framework de moda. É uma forma estruturada de pensar, decidir e agir, validada em mais de 45 projetos desde 2017.",
      cta_primary: "Aplicar STRATFLOW® na sua empresa",
      cta_secondary: "Conhecer o HandOff Mapping",
      meta: [
        { k: "Desde", v: "2017" },
        { k: "Projetos validados", v: "45+" },
        { k: "Fundamentos integrados", v: "Lean · Six Sigma · TOC · Design Thinking · PMI" },
      ],
    },
    intro: {
      eyebrow: "O ponto de partida",
      title_a: "Por que estratégia",
      title_em: "deixa de gerar valor.",
      body: [
        "Empresas raramente falham por falta de boas ideias. Falham na transição: entre o plano e a execução, entre o time que pensa e o time que faz, entre o sistema que registra e o sistema que decide. É exatamente nessa passagem que valor se perde - em handoffs mal definidos, fontes de verdade em conflito, métricas que aparecem só no fechamento e cultura que não absorve a mudança.",
        "STRATFLOW® foi desenhada para essa passagem. Conecta direcionadores estratégicos a processos críticos, dados confiáveis e rituais de governança, garantindo que cada decisão tenha rastreabilidade, cada métrica tenha dono e cada projeto se transforme em capacidade permanente da organização.",
      ],
    },
    foundation: {
      eyebrow: "Fundamentos",
      title_a: "Cinco escolas, uma engenharia",
      title_em: "única de transformação.",
      lede:
        "STRATFLOW® é o resultado da integração disciplinada de cinco corpos de conhecimento maduros. Cada um responde a uma pergunta específica que aparece em projetos de transformação reais.",
      pillars: [
        { t: "Lean", d: "Eliminar desperdício, encurtar ciclos, expor o problema. Responde: onde está o desperdício?", icon: Workflow },
        { t: "Six Sigma", d: "Decidir com dados, reduzir variabilidade, controlar o processo. Responde: o processo é estável?", icon: Microscope },
        { t: "TOC (Teoria das Restrições)", d: "Identificar o gargalo, focar onde gera mais resultado. Responde: por onde começar?", icon: Target },
        { t: "Design Thinking", d: "Centrar no usuário, prototipar, iterar com evidência. Responde: a solução resolve a dor real?", icon: BookOpen },
        { t: "PMI / Project Management", d: "Estruturar entrega, governar escopo, risco e cadência. Responde: como entregamos?", icon: ListChecks },
      ],
    },
    movements: {
      eyebrow: "A jornada STRATFLOW®",
      title_a: "Três movimentos.",
      title_em: "Uma jornada.",
      lede:
        "Toda transformação que adotamos atravessa essas três camadas, ainda que em proporções diferentes. Cada movimento gera entregáveis verificáveis e indicadores observáveis.",
      steps: [
        {
          n: "01",
          title: "Clarificar o valor.",
          subtitle: "Direção, métrica e prioridade.",
          desc:
            "Definimos qual valor a organização precisa gerar (financeiro, operacional, regulatório ou cultural) e como isso se desdobra em objetivos, indicadores e iniciativas. O ponto-chave é remover ambiguidade: todo participante entende o por quê e o para quê.",
          outputs: [
            "Direcionadores estratégicos formalizados.",
            "Árvore de valor (objetivo → indicador → iniciativa).",
            "Critérios de priorização explícitos.",
            "Mapa de stakeholders e papéis decisórios.",
          ],
        },
        {
          n: "02",
          title: "Organizar o fluxo.",
          subtitle: "Processos, dados e governança.",
          desc:
            "Redesenhamos processos críticos, definimos governança de portfólio e estabelecemos cadência. Aqui aplicamos o HandOff Mapping para que o valor flua entre áreas, sistemas e níveis sem perda de informação, sem retrabalho e sem dúvida sobre quem é responsável por o quê.",
          outputs: [
            "Processos críticos redesenhados (As-Is → To-Be).",
            "HandOff Map, catálogo de entidades e Matriz CRUD.",
            "Modelo de governança e cadência de rituais.",
            "Backlog priorizado e métricas de fluxo.",
          ],
        },
        {
          n: "03",
          title: "Ancorar na cultura.",
          subtitle: "Rituais, accountability e capacidade.",
          desc:
            "Implantamos rituais, acordos e mecanismos de accountability que consolidam o novo jeito de operar. A meta é que a mudança permaneça depois que entregamos: liderança capacitada, indicadores em tempo real, governança rodando e equipe responsável pela evolução contínua.",
          outputs: [
            "Sistema de gestão à vista.",
            "Rituais de cadência (diários, semanais, mensais).",
            "Capacitação de lideranças e formadores internos.",
            "Indicadores de adoção e maturidade.",
          ],
        },
      ],
    },
    hom: {
      eyebrow: "Tecnologia central · HandOff Mapping",
      title_a: "O método técnico que sustenta",
      title_em: "o segundo movimento.",
      lede:
        "Em projetos com múltiplas plataformas, múltiplos implementadores e camada corporativa de integração, a maior fonte de retrabalho é o que chamamos de handoff: o momento em que a informação passa de uma área, sistema ou estágio para outro. STRATFLOW® formaliza essa passagem por meio de um padrão chamado HandOff Mapping.",
      definition:
        "HandOff Mapping é o padrão metodológico do STRATFLOW® para mapear processos, estruturar dados e converter processos aprovados em requisitos de integração, contratos de dados, regras de sincronização e critérios de aceite. Reduz ambiguidade entre as frentes de processo, software e integração.",
      principles_title: "Princípios fundamentais",
      principles: [
        { t: "Process-first", d: "A integração nasce do processo aprovado e da decisão de negócio, nunca da disponibilidade técnica de um campo ou conector." },
        { t: "Single Source of Truth", d: "Cada informação crítica tem uma fonte primária definida, uma fonte de consumo clara e uma regra de reuso documentada." },
        { t: "Digital Thread", d: "Todo handoff é rastreável do evento de origem ao efeito no processo subsequente. Rastreabilidade ponta a ponta." },
        { t: "API-first canônica", d: "Integrações são projetadas em torno de objetos de negócio e contratos de dados, não em torno de telas ou extrações manuais." },
        { t: "Clean Core", d: "Privilegiamos configuração, parametrização e integração suportada, evitando customizações que não se sustentam no tempo." },
        { t: "Evidência antes de aceite", d: "Nenhum requisito é considerado pronto sem validação de processo, objeto, atributo, regra e exceção." },
      ],
      levels_title: "Sete níveis lógicos encadeados",
      levels_lede:
        "A técnica trabalha com sete níveis lógicos encadeados. Cada nível responde a uma pergunta específica e elimina um tipo característico de ambiguidade.",
      levels: [
        { n: "01", t: "Processo", d: "Qual macroprocesso ou capacidade está sendo endereçado." },
        { n: "02", t: "Etapa", d: "Qual estágio operacional, gate ou rito está sendo executado." },
        { n: "03", t: "Ação / HandOff", d: "Qual interação altera o estado do processo ou transfere responsabilidade." },
        { n: "04", t: "Dado", d: "Qual informação precisa existir para que a ação ocorra e seja verificável." },
        { n: "05", t: "Entidade", d: "Qual objeto de negócio materializa esse dado." },
        { n: "06", t: "Atributo", d: "Quais campos mínimos tornam a entidade utilizável, confiável e integrável." },
        { n: "07", t: "CRUD / Regra", d: "Quem cria, lê, atualiza, encerra, consome e sincroniza." },
      ],
      steps_title: "Os nove passos da técnica",
      steps_lede: "Cada passo gera entregáveis intermediários que alimentam o próximo, formando um pipeline auditável de evidências.",
      steps: [
        { n: "01", t: "Definir o recorte e a pergunta de negócio.", d: "Formalizar o escopo (início, fim, atores, sistemas, ondas) e a pergunta que a integração precisa responder. Registrar explicitamente o que está fora do escopo." },
        { n: "02", t: "Inventariar o fluxo do processo.", d: "Mapear macroetapas, gates, handoffs e eventos críticos. Separar rito gerencial, ação operacional, evento sistêmico e exceção." },
        { n: "03", t: "Identificar atores, papéis e plataformas por etapa.", d: "Registrar dono, executor, aprovador, consumidor da informação e tratador de exceção. Sinalizar entradas duplas, planilhas paralelas, controles offline." },
        { n: "04", t: "Mapear handoffs.", d: "Para cada handoff: quem entrega o quê, para quem, sob qual condição, com qual efeito. Classificar o tipo (documental, transacional, decisório, evidencial, geoespacial, financeiro, contratual, regulatório)." },
        { n: "05", t: "Derivar entidades de negócio.", d: "Transformar handoffs e saídas de etapa em objetos canônicos. Eliminar duplicidades semânticas. Determinar relações entre entidades mestras, transacionais e derivadas." },
        { n: "06", t: "Derivar atributos mínimos por entidade.", d: "Identificar atributos obrigatórios para criação, uso, integração, rastreabilidade e governança. Classificar tipo (identificador, status, valor, classificação, referência externa, localização, data, evidência, calculado)." },
        { n: "07", t: "Construir a Matriz CRUD.", d: "Registrar, por etapa e plataforma, quem cria (C), lê (R), atualiza (U), aprova, consome e encerra (D/Close). Separar CRUD de negócio do CRUD técnico." },
        { n: "08", t: "Converter a matriz em requisitos de integração.", d: "Transformar conflitos de CRUD, fontes duplicadas de verdade ou leituras fora do sistema em requisitos de integração. Definir direção, gatilho, payload mínimo, frequência, SLA lógico, exceções, reconciliação e monitoramento." },
        { n: "09", t: "Validar, consolidar e versionar.", d: "Executar oficinas de validação com processo, especialistas de plataforma e integração. Consolidar pendências, decisões e critérios de aceite. Congelar a baseline antes do desenho detalhado de integração." },
      ],
      artifacts_title: "Os artefatos do método",
      artifacts_lede:
        "STRATFLOW® gera artefatos versionados que conectam decisão de negócio à implementação técnica. Cada artefato tem propósito definido, dono claro e critério de aceite.",
      artifacts: [
        { t: "HandOff Map", d: "Mapa estruturado de etapas, papéis, entradas, saídas, entidades, atributos e gatilhos." },
        { t: "Catálogo de entidades e atributos", d: "Dicionário de dados de negócio com classificação, criticidade, cardinalidade e fonte de verdade." },
        { t: "Matriz CRUD", d: "Visão por etapa, papel e plataforma da responsabilidade por cada entidade e atributo." },
        { t: "Matriz de sistemas e responsabilidades", d: "Plataformas envolvidas, seus papéis e o padrão de integração esperado." },
        { t: "Catálogo de requisitos de integração", d: "Necessidades de sincronização, evento, payload, frequência, regra de atualização e tratamento de erro." },
        { t: "Pacote de validação e aceite", d: "Roteiros de revisão, critérios mínimos, evidências e plano de teste integrado de negócio." },
      ],
      example_title: "Exemplo prático",
      example_lede:
        "Para tornar concreto, considere a integração entre um software de planejamento (Oracle Primavera Cloud) e um sistema de gestão de projetos (Oracle Unifier).",
      example_situation:
        "O time de planejamento aprova uma baseline tática. Essa informação precisa chegar ao sistema de gestão de projetos para governança, acompanhamento, controle de desvios e relatórios executivos. Atualizações sobre progresso físico, mudanças de baseline e tratamento de restrições precisam ser refletidas coerentemente entre os dois sistemas.",
      example_result:
        "A aplicação do HandOff Mapping elimina conflito de SSOT, evita entrada dupla de dados, define payload mínimo de cada integração, melhora o desenho dos testes integrados e reduz o retrabalho entre implementadores das plataformas e a equipe de integração corporativa.",
      crud_caption: "Matriz CRUD simplificada (extrato)",
      crud_headers: ["Entidade", "Papel", "Plataforma", "C", "R", "U", "Close"],
      crud_rows: [
        ["Cronograma do projeto", "Planejamento", "Oracle Primavera Cloud", "X", "X", "X", ""],
        ["Cronograma do projeto", "PMO / Controles", "Oracle Unifier", "", "X", "", ""],
        ["Avanço físico", "Planejamento", "Oracle Primavera Cloud", "X", "X", "X", ""],
        ["Restrição crítica", "Planejamento", "Oracle Primavera Cloud", "X", "X", "X", ""],
        ["Restrição crítica", "PMO / Liderança", "Oracle Unifier", "", "X", "", "X"],
        ["Decisão de governança", "PMO / Liderança", "Oracle Unifier", "X", "X", "X", "X"],
      ],
      workshops_title: "Padrão de oficinas",
      workshops_lede:
        "O HandOff Mapping é construído em ciclo de oficinas com participação integrada das frentes de processo, plataforma e integração. A cadência típica é:",
      workshops: [
        { o: "Oficina 1 · Recorte", obj: "Confirmar fronteira, objetivo e ondas.", out: "Processo-alvo, fronteiras e premissas" },
        { o: "Oficina 2 · Fluxo", obj: "Mapear etapas, gates e handoffs.", out: "Fluxo validado e handoffs críticos" },
        { o: "Oficina 3 · Dados", obj: "Derivar entidades e atributos.", out: "Catálogo preliminar de entidades e atributos" },
        { o: "Oficina 4 · CRUD", obj: "Validar responsabilidade por criação, leitura e atualização.", out: "Matriz CRUD validada" },
        { o: "Oficina 5 · Integração", obj: "Converter o mapa em requisitos e testes.", out: "Catálogo de requisitos e cenários de teste" },
        { o: "Oficina 6 · Aceite", obj: "Congelar baseline e pendências.", out: "Versão aprovada e backlog residual" },
      ],
      acceptance_title: "Critérios de qualidade e aceite",
      acceptance: [
        "Todo handoff possui dono, entrada, saída, evidência e condição de conclusão.",
        "Toda entidade crítica possui definição, fonte de verdade e atributos mínimos.",
        "Não existem duas fontes de verdade não resolvidas para um mesmo atributo crítico.",
        "A Matriz CRUD é coerente com o fluxo e com a governança do processo.",
        "Regras de integração são derivadas de handoffs reais, não apenas de disponibilidade técnica.",
        "Exceções, pendências, restrições e premissas estão documentadas.",
        "Há rastreabilidade entre processo, entidade, atributo, requisito de integração e teste.",
      ],
    },
    apply: {
      eyebrow: "Onde aplicar",
      title_a: "Quando o STRATFLOW®",
      title_em: "gera mais valor.",
      lede:
        "STRATFLOW® é aplicável a qualquer iniciativa em que processo, dado e cultura precisam evoluir juntos. Os contextos abaixo são onde a metodologia mais reduz risco e acelera resultado:",
      contexts: [
        "Programas de transformação digital com múltiplas plataformas e múltiplos implementadores.",
        "Implantação de PMOs estratégicos ou conversão de PMO em VMO (Value Management Office).",
        "Integração entre planejamento, controle, contratos, medições, ativos e ERP.",
        "Desenho e implementação de centros de excelência (engenharia, operações, dados).",
        "Onboarding de projetos em andamento e migração controlada de controles existentes.",
        "Estruturação de novos produtos ou linhas de negócio com integração regulatória.",
        "Diagnósticos de maturidade em estratégia, processos, digitalização e cultura.",
      ],
    },
    results: {
      eyebrow: "Resultados observados",
      title_a: "Métricas reais.",
      title_em: "Setores diferentes.",
      lede: "Uma amostra dos resultados que clientes obtiveram após aplicarem STRATFLOW®. Números preservados, contexto real.",
      items: [
        { v: "+42%", l: "Eficiência operacional", d: "Indústria de transformação e reciclagem" },
        { v: "+132%", l: "Crescimento em 3 anos", d: "Sustentado por planejamento vivo e cadência" },
        { v: "94%", l: "Operação digitalizada", d: "Construção e infraestrutura, em 3 anos" },
        { v: "+38%", l: "Eficiência de mobilização", d: "Após transformar PMO em VMO" },
        { v: "12→3", l: "Documentação (dias)", d: "Eletromecânica, 100% de aderência" },
        { v: "93%", l: "Aderência ao plano", d: "SMO e governança estratégica" },
      ],
    },
    cta: {
      title_a: "Pronto para aplicar STRATFLOW®",
      title_em: "na sua organização?",
      lede:
        "Em uma conversa inicial de 30 minutos, entendemos seu contexto e mostramos como o método se aplica ao seu desafio específico.",
      primary: "Iniciar uma conversa",
      secondary: "Ler o caso de aplicação",
    },
  },
  en: {
    seo: {
      title: "STRATFLOW® - Business Transformation Methodology | GT Strategy",
      description:
        "STRATFLOW® is GT Strategy's proprietary methodology for business transformation. It combines Lean, Six Sigma, TOC, Design Thinking and PMI into three movements: clarify the value, organize the flow and anchor in culture. Includes HandOff Mapping for process, data and platform integration.",
    },
    nav_back: "Back to home",
    hero: {
      eyebrow: "Proprietary methodology",
      title_a: "STRATFLOW",
      title_em: "®",
      title_b: "The value engineering",
      title_c: "behind transformation.",
      lede:
        "STRATFLOW® is the methodology GT Strategy applies to connect strategy, operations, technology and people into a single value flow. Not a trendy framework, but a structured way to think, decide and act - validated in 45+ projects since 2017.",
      cta_primary: "Apply STRATFLOW® at your company",
      cta_secondary: "Discover HandOff Mapping",
      meta: [
        { k: "Since", v: "2017" },
        { k: "Validated projects", v: "45+" },
        { k: "Integrated foundations", v: "Lean · Six Sigma · TOC · Design Thinking · PMI" },
      ],
    },
    intro: {
      eyebrow: "The starting point",
      title_a: "Why strategy",
      title_em: "stops creating value.",
      body: [
        "Companies rarely fail for lack of good ideas. They fail in the transition: between plan and execution, between the team that thinks and the team that delivers, between the system that records and the system that decides. Value is lost exactly in that passage - in poorly defined handoffs, conflicting sources of truth, metrics that only appear at closing and culture that does not absorb the change.",
        "STRATFLOW® was designed for that passage. It connects strategic drivers to critical processes, reliable data and governance rituals, ensuring every decision is traceable, every metric has an owner and every project becomes a permanent organizational capability.",
      ],
    },
    foundation: {
      eyebrow: "Foundations",
      title_a: "Five schools, one engineered",
      title_em: "transformation method.",
      lede:
        "STRATFLOW® is the result of disciplined integration of five mature bodies of knowledge. Each answers a specific question that surfaces in real transformation projects.",
      pillars: [
        { t: "Lean", d: "Eliminate waste, shorten cycles, expose the problem. Answers: where is the waste?", icon: Workflow },
        { t: "Six Sigma", d: "Decide with data, reduce variability, control the process. Answers: is the process stable?", icon: Microscope },
        { t: "TOC (Theory of Constraints)", d: "Identify the bottleneck, focus where impact is greatest. Answers: where to start?", icon: Target },
        { t: "Design Thinking", d: "User-centered, prototype, iterate with evidence. Answers: does the solution address the real pain?", icon: BookOpen },
        { t: "PMI / Project Management", d: "Structure delivery, govern scope, risk and cadence. Answers: how do we deliver?", icon: ListChecks },
      ],
    },
    movements: {
      eyebrow: "The STRATFLOW® journey",
      title_a: "Three movements.",
      title_em: "One journey.",
      lede:
        "Every transformation we lead goes through these three layers, even if in different proportions. Each movement produces verifiable artifacts and observable indicators.",
      steps: [
        { n: "01", title: "Clarify the value.", subtitle: "Direction, metric and priority.", desc: "We define the value the organization needs to generate (financial, operational, regulatory or cultural) and how it unfolds into objectives, indicators and initiatives. The key is removing ambiguity: every participant understands the why and the what for.", outputs: ["Formalized strategic drivers.", "Value tree (objective → indicator → initiative).", "Explicit prioritization criteria.", "Stakeholder and decision-maker map."] },
        { n: "02", title: "Organize the flow.", subtitle: "Processes, data and governance.", desc: "We redesign critical processes, define portfolio governance and establish cadence. Here we apply HandOff Mapping so value flows across areas, systems and levels without information loss, without rework and without doubt about who owns what.", outputs: ["Critical processes redesigned (As-Is → To-Be).", "HandOff Map, entity catalog and CRUD Matrix.", "Governance model and ritual cadence.", "Prioritized backlog and flow metrics."] },
        { n: "03", title: "Anchor in culture.", subtitle: "Rituals, accountability and capability.", desc: "We install rituals, agreements and accountability mechanisms that consolidate the new way of operating. Goal: change remains after we deliver - trained leadership, real-time indicators, governance running and the team owning continuous evolution.", outputs: ["Visual management system.", "Cadence rituals (daily, weekly, monthly).", "Leadership and internal-trainer capability.", "Adoption and maturity indicators."] },
      ],
    },
    hom: {
      eyebrow: "Core technology · HandOff Mapping",
      title_a: "The technical method that sustains",
      title_em: "the second movement.",
      lede:
        "In projects with multiple platforms, multiple implementers and corporate integration layers, the largest source of rework is what we call a handoff: the moment information passes from one area, system or stage to another. STRATFLOW® formalizes that passage through a standard called HandOff Mapping.",
      definition:
        "HandOff Mapping is the STRATFLOW® methodological standard to map processes, structure data and convert approved processes into integration requirements, data contracts, synchronization rules and acceptance criteria. It reduces ambiguity across process, software and integration fronts.",
      principles_title: "Foundational principles",
      principles: [
        { t: "Process-first", d: "Integration is born from the approved process and the business decision, never from the technical availability of a field or connector." },
        { t: "Single Source of Truth", d: "Every critical piece of information has a defined primary source, a clear consumption source and a documented reuse rule." },
        { t: "Digital Thread", d: "Every handoff is traceable from the origin event to its effect in the downstream process. End-to-end traceability." },
        { t: "Canonical API-first", d: "Integrations are designed around business objects and data contracts, not around screens or manual extractions." },
        { t: "Clean Core", d: "We favor configuration, parametrization and supported integration over unsustainable customizations." },
        { t: "Evidence before acceptance", d: "No requirement is considered ready without validation of process, object, attribute, rule and exception." },
      ],
      levels_title: "Seven chained logical levels",
      levels_lede:
        "The technique works with seven chained logical levels. Each level answers a specific question and eliminates a characteristic type of ambiguity.",
      levels: [
        { n: "01", t: "Process", d: "Which macro-process or capability is being addressed." },
        { n: "02", t: "Stage", d: "Which operational stage, gate or rite is being executed." },
        { n: "03", t: "Action / HandOff", d: "Which interaction changes the process state or transfers responsibility." },
        { n: "04", t: "Data", d: "Which information needs to exist so the action can occur and be verifiable." },
        { n: "05", t: "Entity", d: "Which business object materializes that data." },
        { n: "06", t: "Attribute", d: "Which minimum fields make the entity usable, reliable and integrable." },
        { n: "07", t: "CRUD / Rule", d: "Who creates, reads, updates, closes, consumes and synchronizes." },
      ],
      steps_title: "The nine steps of the technique",
      steps_lede: "Each step produces intermediate deliverables that feed the next, forming an auditable evidence pipeline.",
      steps: [
        { n: "01", t: "Define the scope and the business question.", d: "Formalize the scope (start, end, actors, systems, waves) and the question the integration needs to answer. Explicitly record what is out of scope." },
        { n: "02", t: "Inventory the process flow.", d: "Map macro-stages, gates, handoffs and critical events. Separate managerial rites, operational actions, system events and exceptions." },
        { n: "03", t: "Identify actors, roles and platforms per stage.", d: "Register owner, executor, approver, consumer and exception handler. Flag double data entry, parallel spreadsheets and offline controls." },
        { n: "04", t: "Map handoffs.", d: "For each handoff: who delivers what, to whom, under what condition, with what effect. Classify the type (documental, transactional, decisional, evidential, geospatial, financial, contractual, regulatory)." },
        { n: "05", t: "Derive business entities.", d: "Turn handoffs and stage outputs into canonical objects. Eliminate semantic duplicates. Determine relations between master, transactional and derived entities." },
        { n: "06", t: "Derive minimum attributes per entity.", d: "Identify attributes mandatory for creation, use, integration, traceability and governance. Classify type (identifier, status, value, classification, external reference, location, date, evidence, calculated)." },
        { n: "07", t: "Build the CRUD Matrix.", d: "Record, per stage and platform, who creates (C), reads (R), updates (U), approves, consumes and closes (D/Close). Separate business CRUD from technical CRUD." },
        { n: "08", t: "Convert the matrix into integration requirements.", d: "Turn CRUD conflicts, duplicated sources of truth or off-system reads into integration requirements. Define direction, trigger, minimum payload, frequency, logical SLA, exceptions, reconciliation and monitoring." },
        { n: "09", t: "Validate, consolidate and version.", d: "Run validation workshops with process, platform specialists and integration. Consolidate pending items, decisions and acceptance criteria. Freeze the baseline before detailed integration design." },
      ],
      artifacts_title: "The method artifacts",
      artifacts_lede:
        "STRATFLOW® produces versioned artifacts that connect business decision to technical implementation. Each artifact has a defined purpose, a clear owner and acceptance criteria.",
      artifacts: [
        { t: "HandOff Map", d: "Structured map of stages, roles, inputs, outputs, entities, attributes and triggers." },
        { t: "Entity and attribute catalog", d: "Business data dictionary with classification, criticality, cardinality and source of truth." },
        { t: "CRUD Matrix", d: "View by stage, role and platform of responsibility for each entity and attribute." },
        { t: "Systems and responsibilities matrix", d: "Platforms involved, their roles and the expected integration standard." },
        { t: "Integration requirements catalog", d: "Synchronization needs, event, payload, frequency, update rule and error handling." },
        { t: "Validation and acceptance package", d: "Review scripts, minimum criteria, evidence and integrated business test plan." },
      ],
      example_title: "Practical example",
      example_lede:
        "To make this concrete, consider the integration between a planning tool (Oracle Primavera Cloud) and a project management system (Oracle Unifier).",
      example_situation:
        "The planning team approves a tactical baseline. That information needs to reach the project management system for governance, tracking, deviation control and executive reporting. Updates on physical progress, baseline changes and constraint handling must be coherently reflected across both systems.",
      example_result:
        "Applying HandOff Mapping eliminates SSOT conflicts, prevents double data entry, defines the minimum payload of each integration, improves integrated test design and reduces rework between platform implementers and the corporate integration team.",
      crud_caption: "Simplified CRUD Matrix (extract)",
      crud_headers: ["Entity", "Role", "Platform", "C", "R", "U", "Close"],
      crud_rows: [
        ["Project schedule", "Planning", "Oracle Primavera Cloud", "X", "X", "X", ""],
        ["Project schedule", "PMO / Controls", "Oracle Unifier", "", "X", "", ""],
        ["Physical progress", "Planning", "Oracle Primavera Cloud", "X", "X", "X", ""],
        ["Critical constraint", "Planning", "Oracle Primavera Cloud", "X", "X", "X", ""],
        ["Critical constraint", "PMO / Leadership", "Oracle Unifier", "", "X", "", "X"],
        ["Governance decision", "PMO / Leadership", "Oracle Unifier", "X", "X", "X", "X"],
      ],
      workshops_title: "Workshop standard",
      workshops_lede:
        "HandOff Mapping is built in a workshop cycle with integrated participation from process, platform and integration fronts. Typical cadence:",
      workshops: [
        { o: "Workshop 1 · Scope", obj: "Confirm boundary, objective and waves.", out: "Target process, boundaries and assumptions" },
        { o: "Workshop 2 · Flow", obj: "Map stages, gates and handoffs.", out: "Validated flow and critical handoffs" },
        { o: "Workshop 3 · Data", obj: "Derive entities and attributes.", out: "Preliminary entity and attribute catalog" },
        { o: "Workshop 4 · CRUD", obj: "Validate creation, read and update responsibility.", out: "Validated CRUD Matrix" },
        { o: "Workshop 5 · Integration", obj: "Turn the map into requirements and tests.", out: "Requirements catalog and test scenarios" },
        { o: "Workshop 6 · Acceptance", obj: "Freeze baseline and pending items.", out: "Approved version and residual backlog" },
      ],
      acceptance_title: "Quality and acceptance criteria",
      acceptance: [
        "Every handoff has owner, input, output, evidence and completion condition.",
        "Every critical entity has definition, source of truth and minimum attributes.",
        "There are no two unresolved sources of truth for the same critical attribute.",
        "The CRUD Matrix is consistent with the flow and process governance.",
        "Integration rules are derived from real handoffs, not just technical availability.",
        "Exceptions, pending items, constraints and assumptions are documented.",
        "There is traceability between process, entity, attribute, integration requirement and test.",
      ],
    },
    apply: {
      eyebrow: "Where to apply",
      title_a: "When STRATFLOW®",
      title_em: "creates the most value.",
      lede:
        "STRATFLOW® applies wherever process, data and culture must evolve together. The contexts below are where the method most reduces risk and accelerates outcome:",
      contexts: [
        "Digital transformation programs with multiple platforms and implementers.",
        "Strategic PMO deployment or PMO to VMO (Value Management Office) conversion.",
        "Integration between planning, control, contracts, measurements, assets and ERP.",
        "Design and implementation of centers of excellence (engineering, operations, data).",
        "Onboarding of in-flight projects and controlled migration of existing controls.",
        "Structuring of new products or business lines with regulatory integration.",
        "Maturity diagnostics across strategy, processes, digitalization and culture.",
      ],
    },
    results: {
      eyebrow: "Observed results",
      title_a: "Real metrics.",
      title_em: "Different sectors.",
      lede: "A sample of the outcomes our clients achieved after applying STRATFLOW®. Figures preserved, context is real.",
      items: [
        { v: "+42%", l: "Operational efficiency", d: "Transformation and recycling industry" },
        { v: "+132%", l: "Growth in 3 years", d: "Sustained by living plan and cadence" },
        { v: "94%", l: "Operations digitalized", d: "Construction and infrastructure, in 3 years" },
        { v: "+38%", l: "Mobilization efficiency", d: "After turning PMO into VMO" },
        { v: "12→3", l: "Documentation (days)", d: "Electromechanical, 100% adherence" },
        { v: "93%", l: "Plan adherence", d: "SMO and strategic governance" },
      ],
    },
    cta: {
      title_a: "Ready to apply STRATFLOW®",
      title_em: "at your organization?",
      lede:
        "In an initial 30-minute conversation we understand your context and show how the method applies to your specific challenge.",
      primary: "Start a conversation",
      secondary: "Read the application case",
    },
  },
};

// ==================== Helpers ====================
const SectionTitle = ({ eyebrow, a, em, lede }) => (
  <div className="grid lg:grid-cols-12 gap-10 mb-14">
    <div className="lg:col-span-7">
      <div className="section-eyebrow mb-6">
        <span className="gt-eyebrow">{eyebrow}</span>
      </div>
      <h2 className="gt-display text-gt-cream text-3xl md:text-5xl lg:text-6xl">
        {a} <span className="block text-gt-cream/55 gt-display-light">{em}</span>
      </h2>
    </div>
    {lede ? (
      <div className="lg:col-span-5 flex items-end">
        <p className="text-base md:text-lg text-gt-ink/75 leading-relaxed">{lede}</p>
      </div>
    ) : null}
  </div>
);

// ==================== Page ====================
export default function StratflowPage() {
  const { lang, t: globalT } = useLang();
  const t = C[lang] || C.pt;

  const ldJson = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "STRATFLOW® — Metodologia de Transformação de Negócios",
    description: t.seo.description,
    author: { "@type": "Organization", name: "GT Strategy", url: SITE },
    publisher: {
      "@type": "Organization",
      name: "GT Strategy",
      logo: { "@type": "ImageObject", url: `${SITE}/brand/logo-gt-slogan.svg` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/stratflow` },
    inLanguage: lang === "pt" ? "pt-BR" : "en",
    image: `${SITE}/brand/logo-gt-slogan.svg`,
    keywords:
      "STRATFLOW, metodologia, transformação, consultoria, HandOff Mapping, Lean, Six Sigma, TOC, Design Thinking, PMI, GT Strategy, integração de processos, Matriz CRUD, governança",
    about: [
      { "@type": "Thing", name: "Business transformation methodology" },
      { "@type": "Thing", name: "HandOff Mapping" },
      { "@type": "Thing", name: "Lean Six Sigma" },
      { "@type": "Thing", name: "PMO / VMO" },
    ],
  };

  usePageMeta({
    title: t.seo.title,
    description: t.seo.description,
    canonical: `${SITE}/stratflow`,
    image: `${SITE}/brand/logo-gt-slogan.svg`,
    type: "article",
    ldJson,
  });

  return (
    <main className="bg-gt-bg text-gt-ink min-h-screen overflow-x-hidden">
      <Header />

      {/* ===== HERO ===== */}
      <section className="relative pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden" data-testid="stratflow-hero">
        <div className="absolute inset-0 flex items-center justify-end pointer-events-none select-none">
          <img
            src="/brand/logo-gt-slogan.svg"
            alt=""
            className="h-[110%] w-auto opacity-[0.04]"
            style={{ filter: "brightness(2)" }}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-gt-bg via-gt-bg/85 to-transparent" />
        <div className="gt-grain absolute inset-0" />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Link
            to="/"
            data-testid="back-home"
            className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.25em] text-gt-mute hover:text-gt-cream transition-colors mb-10"
          >
            <span>←</span> {t.nav_back}
          </Link>
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-9 gt-reveal">
              <div className="section-eyebrow mb-8">
                <span className="gt-eyebrow">{t.hero.eyebrow}</span>
              </div>
              <h1 className="gt-display text-gt-cream text-[2.6rem] sm:text-[3.6rem] md:text-[5rem] lg:text-[6.5rem]" data-testid="stratflow-h1">
                <span className="block">
                  {t.hero.title_a}
                  <sup className="text-[0.4em] align-super text-gt-cream/70 ml-1">{t.hero.title_em}</sup>
                </span>
                <span className="block gt-display-light text-gt-cream/55 mt-2 text-[1.8rem] sm:text-[2.4rem] md:text-[3rem] lg:text-[3.6rem]">
                  {t.hero.title_b}
                </span>
                <span className="block gt-display-light text-gt-cream/55 text-[1.8rem] sm:text-[2.4rem] md:text-[3rem] lg:text-[3.6rem]">
                  {t.hero.title_c}
                </span>
              </h1>
              <p className="mt-10 text-base md:text-xl text-gt-ink/80 max-w-3xl leading-relaxed">{t.hero.lede}</p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/#contact"
                  data-testid="stratflow-cta-primary"
                  className="group inline-flex items-center justify-between gap-4 px-7 py-4 text-xs btn-cream min-w-[260px]"
                >
                  {t.hero.cta_primary}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="#hom"
                  data-testid="stratflow-cta-secondary"
                  className="group inline-flex items-center justify-between gap-4 px-7 py-4 text-xs btn-outline-cream min-w-[260px]"
                >
                  {t.hero.cta_secondary}
                  <span aria-hidden>↓</span>
                </a>
              </div>

              <dl className="mt-14 grid sm:grid-cols-3 gap-px bg-gt-cream/8 max-w-3xl">
                {t.hero.meta.map((m, i) => (
                  <div key={i} className="bg-gt-bg p-5">
                    <dt className="text-[0.6rem] uppercase tracking-[0.22em] text-gt-mute mb-2">{m.k}</dt>
                    <dd className="font-display text-gt-cream uppercase text-lg leading-tight">{m.v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* ===== INTRO ===== */}
      <section className="py-24 md:py-32 border-t border-gt-cream/8" data-testid="stratflow-intro">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <SectionTitle eyebrow={t.intro.eyebrow} a={t.intro.title_a} em={t.intro.title_em} />
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8 space-y-6 text-base md:text-lg text-gt-ink/80 leading-relaxed">
              {t.intro.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOUNDATION ===== */}
      <section className="py-24 md:py-32 border-t border-gt-cream/8" style={{ backgroundColor: "#071614" }} data-testid="stratflow-foundation">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <SectionTitle eyebrow={t.foundation.eyebrow} a={t.foundation.title_a} em={t.foundation.title_em} lede={t.foundation.lede} />
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-gt-cream/8">
            {t.foundation.pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="bg-gt-bg p-7 min-h-[260px] flex flex-col">
                  <Icon size={24} strokeWidth={1.2} className="text-gt-cream mb-5" />
                  <h3 className="font-display text-gt-cream text-xl uppercase leading-tight mb-3">{p.t}</h3>
                  <p className="text-sm text-gt-ink/65 leading-relaxed mt-auto">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== MOVEMENTS ===== */}
      <section className="py-24 md:py-32 border-t border-gt-cream/8" data-testid="stratflow-movements">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <SectionTitle eyebrow={t.movements.eyebrow} a={t.movements.title_a} em={t.movements.title_em} lede={t.movements.lede} />
          <div className="space-y-px bg-gt-cream/8">
            {t.movements.steps.map((s, i) => (
              <div key={s.n} className="bg-gt-bg p-8 md:p-12 grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-3">
                  <div className="font-display text-7xl md:text-8xl text-gt-cream/30 leading-none">{s.n}</div>
                  <div className="text-[0.65rem] uppercase tracking-[0.22em] text-gt-cream/70 mt-4">{s.subtitle}</div>
                </div>
                <div className="lg:col-span-5">
                  <h3 className="font-display text-gt-cream text-2xl md:text-3xl uppercase leading-tight mb-4">{s.title}</h3>
                  <p className="text-base text-gt-ink/75 leading-relaxed">{s.desc}</p>
                </div>
                <div className="lg:col-span-4">
                  <div className="text-[0.6rem] uppercase tracking-[0.22em] text-gt-mute mb-3">
                    {lang === "pt" ? "Entregáveis" : "Deliverables"}
                  </div>
                  <ul className="space-y-2">
                    {s.outputs.map((o, j) => (
                      <li key={j} className="flex gap-2 text-sm text-gt-ink/80">
                        <CheckCircle2 size={14} className="text-gt-cream/70 shrink-0 mt-1" />
                        <span className="leading-relaxed">{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HANDOFF MAPPING ===== */}
      <section id="hom" className="border-t border-gt-cream/8" style={{ backgroundColor: "#071614" }} data-testid="stratflow-hom">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <SectionTitle eyebrow={t.hom.eyebrow} a={t.hom.title_a} em={t.hom.title_em} lede={t.hom.lede} />

          <div className="max-w-4xl border-l border-gt-cream/30 pl-8 py-2 mb-20">
            <p className="font-display text-gt-cream text-xl md:text-2xl leading-snug uppercase">{t.hom.definition}</p>
          </div>

          {/* Principles */}
          <div className="mb-24">
            <h3 className="font-display text-gt-cream text-2xl md:text-3xl uppercase mb-8">{t.hom.principles_title}</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-gt-cream/8">
              {t.hom.principles.map((p, i) => (
                <div key={i} className="bg-gt-bg p-7 min-h-[180px]">
                  <div className="font-display text-2xl text-gt-cream/40 mb-3">0{i + 1}</div>
                  <h4 className="font-display text-gt-cream text-lg uppercase leading-tight mb-2">{p.t}</h4>
                  <p className="text-sm text-gt-ink/65 leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 7 Levels */}
          <div className="mb-24">
            <h3 className="font-display text-gt-cream text-2xl md:text-3xl uppercase mb-4">{t.hom.levels_title}</h3>
            <p className="text-gt-ink/75 max-w-3xl mb-10 leading-relaxed">{t.hom.levels_lede}</p>
            <div className="space-y-px bg-gt-cream/8">
              {t.hom.levels.map((l, i) => (
                <div key={l.n} className="bg-gt-bg p-6 md:p-8 grid grid-cols-12 gap-6 items-center">
                  <div className="col-span-2 md:col-span-1 font-display text-3xl md:text-4xl text-gt-cream/40">{l.n}</div>
                  <div className="col-span-10 md:col-span-3 font-display text-gt-cream text-lg md:text-xl uppercase">{l.t}</div>
                  <div className="col-span-12 md:col-span-8 text-sm md:text-base text-gt-ink/70">{l.d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 9 Steps */}
          <div className="mb-24">
            <h3 className="font-display text-gt-cream text-2xl md:text-3xl uppercase mb-4">{t.hom.steps_title}</h3>
            <p className="text-gt-ink/75 max-w-3xl mb-10 leading-relaxed">{t.hom.steps_lede}</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-gt-cream/8">
              {t.hom.steps.map((s) => (
                <div key={s.n} className="bg-gt-bg p-7 min-h-[220px] flex flex-col">
                  <div className="font-display text-3xl text-gt-cream/40 mb-4">{s.n}</div>
                  <h4 className="font-display text-gt-cream text-lg uppercase leading-tight mb-3">{s.t}</h4>
                  <p className="text-sm text-gt-ink/65 leading-relaxed mt-auto">{s.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Artifacts */}
          <div className="mb-24">
            <h3 className="font-display text-gt-cream text-2xl md:text-3xl uppercase mb-4">{t.hom.artifacts_title}</h3>
            <p className="text-gt-ink/75 max-w-3xl mb-10 leading-relaxed">{t.hom.artifacts_lede}</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-gt-cream/8">
              {t.hom.artifacts.map((a, i) => (
                <div key={i} className="bg-gt-bg p-7 min-h-[180px]">
                  <Database size={20} strokeWidth={1.3} className="text-gt-cream mb-4" />
                  <h4 className="font-display text-gt-cream text-lg uppercase leading-tight mb-2">{a.t}</h4>
                  <p className="text-sm text-gt-ink/65 leading-relaxed">{a.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Example */}
          <div className="mb-24 border border-gt-cream/10 bg-gt-bg/40 p-8 md:p-12">
            <div className="text-[0.65rem] uppercase tracking-[0.25em] text-gt-cream/70 mb-4">
              {lang === "pt" ? "Estudo de aplicação" : "Application study"}
            </div>
            <h3 className="font-display text-gt-cream text-2xl md:text-3xl uppercase mb-5">{t.hom.example_title}</h3>
            <p className="text-gt-ink/75 leading-relaxed mb-5 max-w-4xl">{t.hom.example_lede}</p>
            <p className="text-gt-ink/70 leading-relaxed mb-10 max-w-4xl">{t.hom.example_situation}</p>

            {/* CRUD table */}
            <div className="mb-8 overflow-x-auto -mx-2 px-2">
              <div className="text-[0.6rem] uppercase tracking-[0.22em] text-gt-mute mb-3">{t.hom.crud_caption}</div>
              <table className="min-w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gt-cream/20">
                    {t.hom.crud_headers.map((h, i) => (
                      <th key={i} className="text-left p-3 font-display uppercase text-xs text-gt-cream tracking-[0.12em]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {t.hom.crud_rows.map((row, i) => (
                    <tr key={i} className="border-b border-gt-cream/8 hover:bg-gt-surface/30 transition-colors">
                      {row.map((cell, j) => (
                        <td key={j} className={`p-3 ${j === 0 ? "text-gt-cream/90" : j >= 3 ? "text-center font-display text-gt-cream" : "text-gt-ink/70"}`}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-gt-ink/75 leading-relaxed max-w-4xl">{t.hom.example_result}</p>
          </div>

          {/* Workshops */}
          <div className="mb-24">
            <h3 className="font-display text-gt-cream text-2xl md:text-3xl uppercase mb-4">{t.hom.workshops_title}</h3>
            <p className="text-gt-ink/75 max-w-3xl mb-10 leading-relaxed">{t.hom.workshops_lede}</p>
            <div className="space-y-px bg-gt-cream/8">
              {t.hom.workshops.map((w, i) => (
                <div key={i} className="bg-gt-bg p-6 grid md:grid-cols-12 gap-4 md:gap-6 items-start">
                  <div className="md:col-span-3 font-display text-gt-cream uppercase text-base">{w.o}</div>
                  <div className="md:col-span-5 text-sm text-gt-ink/75 leading-relaxed">{w.obj}</div>
                  <div className="md:col-span-4 text-[0.7rem] uppercase tracking-[0.18em] text-gt-cream/70">{w.out}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Acceptance */}
          <div>
            <h3 className="font-display text-gt-cream text-2xl md:text-3xl uppercase mb-8">{t.hom.acceptance_title}</h3>
            <ul className="grid md:grid-cols-2 gap-x-10 gap-y-4 max-w-5xl">
              {t.hom.acceptance.map((a, i) => (
                <li key={i} className="flex gap-3 items-start text-sm md:text-base text-gt-ink/80">
                  <ShieldCheck size={18} className="text-gt-cream/80 shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== APPLY ===== */}
      <section className="py-24 md:py-32 border-t border-gt-cream/8" data-testid="stratflow-apply">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <SectionTitle eyebrow={t.apply.eyebrow} a={t.apply.title_a} em={t.apply.title_em} lede={t.apply.lede} />
          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-gt-cream/8">
            {t.apply.contexts.map((c, i) => (
              <li key={i} className="bg-gt-bg p-6 min-h-[140px] flex gap-4">
                <span className="font-display text-gt-cream/40 text-xl shrink-0">0{i + 1}</span>
                <span className="text-sm md:text-base text-gt-ink/80 leading-relaxed">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== RESULTS ===== */}
      <section className="py-24 md:py-32 border-t border-gt-cream/8" style={{ backgroundColor: "#071614" }} data-testid="stratflow-results">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <SectionTitle eyebrow={t.results.eyebrow} a={t.results.title_a} em={t.results.title_em} lede={t.results.lede} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-gt-cream/8">
            {t.results.items.map((r, i) => (
              <div key={i} className="bg-gt-bg p-8 min-h-[220px] flex flex-col justify-between">
                <div className="text-[0.65rem] uppercase tracking-[0.26em] text-gt-cream/70">{r.l}</div>
                <div className="gt-bignumber text-6xl md:text-7xl my-6">{r.v}</div>
                <p className="text-sm text-gt-ink/65 leading-relaxed">{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-24 md:py-36 border-t border-gt-cream/8" data-testid="stratflow-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <h2 className="gt-display text-gt-cream text-4xl md:text-6xl lg:text-7xl">
                {t.cta.title_a} <span className="block text-gt-cream/55 gt-display-light">{t.cta.title_em}</span>
              </h2>
              <p className="mt-8 text-base md:text-lg text-gt-ink/75 max-w-2xl leading-relaxed">{t.cta.lede}</p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-4">
              <Link
                to="/#contact"
                data-testid="final-cta-primary"
                className="group inline-flex items-center justify-between gap-3 px-7 py-4 text-xs btn-cream"
              >
                {t.cta.primary} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/#cases"
                data-testid="final-cta-secondary"
                className="inline-flex items-center justify-between gap-3 px-7 py-4 text-xs btn-outline-cream"
              >
                {t.cta.secondary} <span>→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
